import { FC, Fragment, useCallback, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtom, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import type { DTOSubscriptionBenefitItem } from 'src/apis/dto/DTOSubscription';
import { SubscriptionHeaderTitle } from 'src/components/AzarOnly/Subscription/styles';
import Button from 'src/components/Button';
import ModalLayoutBase, { MobileModalType, ModalContent, ModalHeader } from 'src/components/ModalLayout';
import ModalFooter from 'src/components/ModalLayout/ModalFooter';
import { useSubscriptionEventData } from 'src/hooks/useSubscription';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { checkoutEventContextAtom } from 'src/stores/subscription/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import BenefitListItem from './BenefitListItem';
interface Props {
  benefits: DTOSubscriptionBenefitItem[];
}
const ModalLayout = styled(ModalLayoutBase)`
  ${({
  theme
}) => theme.screenSize.mobile} {
    max-height: 640px;
  }
`;
const ConfettiOverlay = styled.div`
  position: fixed;
  top: 0;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  width: 300px;
  pointer-events: none;
  z-index: 42;

  ${({
  theme
}) => theme.screenSize.mobile} {
    z-index: 52;
  }

  ${({
  theme
}) => theme.isRtl && `transform: translateX(50%);`}
`;
const ConfettiImage = styled.img`
  width: 100%;
`;
const DoneButton = styled(Button)`
  width: 100%;
  height: 52px;
  border-radius: 360px;
  font-size: 16px;
  background: ${({
  theme
}) => theme.color.primary1};
`;
const SubscriptionSuccessModal: FC<Props> = ({
  benefits
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const subscriptionEventData = useSubscriptionEventData();
  const [eventContext, setEventContext] = useAtom(checkoutEventContextAtom);
  const handleDone = useCallback(() => {
    closeModal(ModalType.SUBSCRIPTION_SUCCESS);
  }, [closeModal]);
  useEffect(() => {
    const {
      button_type,
      current_page_id,
      initial_feature,
      initial_page_id,
      is_intro_price_displayed,
      land_from,
      product_id
    } = eventContext ?? {};
    pushEvent({
      eventType: EVENT_TYPE.SUBSCRIPTION,
      eventName: EVENT_NAME.SUBS__PURCHASE_DONE,
      eventParams: {
        button_type,
        current_page_id,
        initial_feature: initial_feature ?? 'none',
        initial_page_id: initial_page_id ?? current_page_id,
        is_intro_price_displayed,
        land_from: land_from ?? 'none',
        product_id,
        ...subscriptionEventData.eventParams,
        is_store_account_active: false,
        is_store_account_matched: false
      }
    });
    setEventContext(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <Fragment>
      <ConfettiOverlay data-sentry-element="ConfettiOverlay" data-sentry-source-file="index.tsx">
        <ConfettiImage src='/images/subscription/confetti.webp' alt='' data-sentry-element="ConfettiImage" data-sentry-source-file="index.tsx" />
      </ConfettiOverlay>

      <ModalLayout isDim mobileType={MobileModalType.BOTTOM_SHEET} navButtonType={ModalNavButtonType.NONE} onClose={handleDone} data-sentry-element="ModalLayout" data-sentry-source-file="index.tsx">
        <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
          <SubscriptionHeaderTitle data-sentry-element="SubscriptionHeaderTitle" data-sentry-source-file="index.tsx">
            {t('shop.subs_purchase_complete_title')}
          </SubscriptionHeaderTitle>
        </ModalHeader>

        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
          {benefits.map(benefit => <BenefitListItem key={benefit.productName} benefit={benefit} />)}
        </ModalContent>

        <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
          <DoneButton id='subscription-success-done' onClick={handleDone} data-sentry-element="DoneButton" data-sentry-source-file="index.tsx">
            {t('shop.subs_purchase_complete_cta')}
          </DoneButton>
        </ModalFooter>
      </ModalLayout>
    </Fragment>;
};
export default SubscriptionSuccessModal;